import { db, auth } from "../../config/firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// Signature throttling cache to prevent Firestore spamming (max 1 write per unique signature per 30s)
const recentIncidentSignatures = new Map();
const THROTTLE_WINDOW_MS = 30000;

/**
 * Logs a Scratch diagnostic or error incident to Firestore collection 'scratch_error_logs'.
 * This function is non-blocking, completely fail-safe, and never throws or interrupts application execution.
 *
 * @param {string} incidentType - "BLOCK_DELETION_ANOMALY" | "SCRATCH_HANG_TIMEOUT" | "IFRAME_RUNTIME_CRASH" | "SYNC_FALLBACK_TRIGGERED"
 * @param {object} details - Incident details and context
 */
export const logScratchIncident = async (incidentType, details = {}) => {
  try {
    if (!db) return;

    // Deduplication check: build signature based on incident type and core identifier
    const target = details.targetName || "default";
    const msg = details.message || "";
    const signature = `${incidentType}:${target}:${msg}`;
    const now = Date.now();

    const lastFired = recentIncidentSignatures.get(signature);
    if (lastFired && now - lastFired < THROTTLE_WINDOW_MS) {
      return; // Skip throttled duplicate
    }
    recentIncidentSignatures.set(signature, now);

    // Garbage-collect old signatures
    if (recentIncidentSignatures.size > 100) {
      recentIncidentSignatures.forEach((time, key) => {
        if (now - time > THROTTLE_WINDOW_MS) {
          recentIncidentSignatures.delete(key);
        }
      });
    }

    let currentUser = null;
    try {
      currentUser = auth?.currentUser;
    } catch (_) {}

    // Clean and cap code snippet if provided
    let safeCode = details.code || details.codeSnippet || null;
    if (typeof safeCode === "string" && safeCode.length > 1500) {
      safeCode = safeCode.slice(0, 1500) + "\n... [truncated]";
    }

    const payload = {
      incidentType: String(incidentType || "UNKNOWN_INCIDENT"),
      severity: details.severity || "error",
      message: String(msg || "No message provided"),
      userId: currentUser?.uid || "guest",
      userEmail: currentUser?.email || "guest",
      timestamp: serverTimestamp(),
      clientTime: new Date().toISOString(),
      url: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      context: {
        targetName: details.targetName || null,
        activeCodeLang: details.activeCodeLang || details.lang || "python",
        blockCountBefore: typeof details.blockCountBefore === "number" ? details.blockCountBefore : (typeof details.prevCount === "number" ? details.prevCount : null),
        blockCountAfter: typeof details.blockCountAfter === "number" ? details.blockCountAfter : (typeof details.newCount === "number" ? details.newCount : null),
        blocksLost: typeof details.blocksLost === "number" ? details.blocksLost : null,
        codeSnippet: safeCode,
        errorStack: details.errorStack || (details.error?.stack ? String(details.error.stack).slice(0, 1000) : null),
        unparsedLines: Array.isArray(details.unparsedLines) ? details.unparsedLines.slice(0, 10) : null,
        extra: details.extra || null,
      },
    };

    // Non-blocking fire-and-forget write to Firestore
    addDoc(collection(db, "scratch_error_logs"), payload).catch((err) => {
      // Silently ignore telemetry write failures to never break UI
      if (process.env.NODE_ENV === "development") {
        console.warn("[Telemetry] Failed to record incident:", err?.message);
      }
    });
  } catch (outerErr) {
    // Fail-safe: telemetry must never interrupt any application logic
    if (process.env.NODE_ENV === "development") {
      console.warn("[Telemetry] Unexpected error in logScratchIncident:", outerErr);
    }
  }
};
